import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { formatAmount, formatPlural } from 'utils';
import { selectExtraMembersInfo } from 'reducers/selectors/sharedPlans';
import { tierThemes } from '../benefits';
import { selectActiveTier, selectStripePaymentLast4, selectStripePaymentMethodMissing, selectStripePlaidAch, } from '../selectors';
import useStripePayment from './useStripePayment';
import useChangeAchPaymentMethod, { NO_ACH_ERROR, } from './useChangeAchPaymentMethod';
const useFailedPayment = (options) => {
    const seatBased = options?.seatBased;
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const tier = useAppSelector(selectActiveTier);
    const customerLast4Digits = useAppSelector(selectStripePaymentLast4);
    const missingPaymentMethod = useAppSelector(selectStripePaymentMethodMissing);
    const extraMembersInfo = useAppSelector(selectExtraMembersInfo);
    const extraMemberSlotCount = extraMembersInfo?.totalSeats ?? 0;
    const { changePaymentMethod, changingPaymentMethod } = useStripePayment();
    const { start: startChangeAchPaymentMethod, isFetching: changingAchPaymentMethod, } = useChangeAchPaymentMethod();
    const paymentFailed = seatBased
        ? extraMembersInfo?.paymentFailed
        : emmaProStatus.paymentFailed;
    const failedPaymentAmount = seatBased
        ? extraMembersInfo?.failedPaymentAmount
        : emmaProStatus.failedPaymentAmount;
    const { autoRenewPrice } = emmaProStatus;
    const showStripePlaidAch = useAppSelector(selectStripePlaidAch);
    const onPressUpdatePayment = useCallback(async () => {
        if (showStripePlaidAch) {
            try {
                await startChangeAchPaymentMethod();
            }
            catch (e) {
                if (e instanceof Error && e.message === NO_ACH_ERROR) {
                    changePaymentMethod(emmaProStatus, { seatBased });
                }
            }
            return;
        }
        changePaymentMethod(emmaProStatus, { seatBased });
    }, [
        changePaymentMethod,
        emmaProStatus,
        seatBased,
        showStripePlaidAch,
        startChangeAchPaymentMethod,
    ]);
    const tierName = tier ? tierThemes[tier].name : undefined;
    const subtitleText = useMemo(() => {
        const benefitName = seatBased
            ? `Extra member ${formatPlural('slot', 'slots', extraMemberSlotCount)}`
            : tierName ?? 'your benefits';
        if (missingPaymentMethod) {
            return `We are not able to charge you. Please add a payment method as soon as possible so you can keep ${benefitName} without losing access.`;
        }
        const displayPrice = seatBased
            ? failedPaymentAmount
            : failedPaymentAmount ?? autoRenewPrice;
        if (displayPrice) {
            const { amount, currency } = displayPrice;
            const formattedAmount = formatAmount(amount, currency);
            if (customerLast4Digits) {
                return `A payment of ${formattedAmount} to your card ending in ${customerLast4Digits} failed. Please update your payment method to keep ${benefitName}.`;
            }
            return `A payment of ${formattedAmount} failed. Please update your payment method to keep ${benefitName} without losing access.`;
        }
        return `We were unable to charge you. Please update your payment method as soon as possible so you can keep ${benefitName} without losing access.`;
    }, [
        autoRenewPrice,
        customerLast4Digits,
        extraMemberSlotCount,
        failedPaymentAmount,
        missingPaymentMethod,
        seatBased,
        tierName,
    ]);
    const modalSubtitleText = useMemo(() => {
        const displayPrice = seatBased
            ? failedPaymentAmount
            : failedPaymentAmount ?? autoRenewPrice;
        if (displayPrice) {
            const { amount, currency } = displayPrice;
            const formattedAmount = formatAmount(amount, currency);
            if (customerLast4Digits) {
                return `A payment of ${formattedAmount} to your card ending in ${customerLast4Digits} was unsuccessful. Please update your payment method to continue.`;
            }
            return `A payment of ${formattedAmount} was unsuccessful. Please update your payment method to continue.`;
        }
        return 'Please update your payment method to continue.';
    }, [autoRenewPrice, customerLast4Digits, failedPaymentAmount, seatBased]);
    const titleText = missingPaymentMethod
        ? 'Missing payment method'
        : 'Payment failed';
    return {
        paymentFailed,
        missingPaymentMethod,
        changingPaymentMethod: changingPaymentMethod || changingAchPaymentMethod,
        onPressUpdatePayment,
        subtitleText,
        modalSubtitleText,
        titleText,
        hasSeats: !!extraMemberSlotCount,
    };
};
export default useFailedPayment;
